import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, IconButton, Paper, Tooltip, Typography } from '@mui/material';
import { ZoomIn, ZoomOut, Refresh } from '@mui/icons-material';
import { LayoutDefinition, Widget } from '../types';
import { absoluteOrigin, widgetsOf } from '../normalize';
import { lvglNativeRenderFrame } from '../lvglNative';
import { lvglRenderFrame } from '../lvglFrame';
import { hitTestLvglWidget } from '../lvglRender';
import { useI18n } from '../../i18n';

type Props = {
  layout: LayoutDefinition;
  selectedWidgetPath?: number[];
  onWidgetSelect?: (widget: Widget, path: number[]) => void;
  onWidgetChange?: (updatedWidget: Widget, path: number[], deltaX: number, deltaY: number) => void;
  onWidgetDrop?: (path: number[], absX: number, absY: number) => void;
  emptyHint?: React.ReactNode;
};

function widgetAt(widgets: Widget[], path: number[]): Widget | undefined {
  let list = widgets;
  let current: Widget | undefined;
  for (const index of path) {
    current = list[index];
    if (!current) {
      return undefined;
    }
    list = current.children || [];
  }
  return current;
}

/**
 * Pixel canvas editor for LVGL layouts.
 * Prefers the real LVGL 8.3 WASM viewer; falls back to the JS painter.
 */
export const LvglCanvasEditor: React.FC<Props> = ({
  layout,
  selectedWidgetPath,
  onWidgetSelect,
  onWidgetChange,
  onWidgetDrop,
  emptyHint,
}) => {
  const { t } = useI18n();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [zoom, setZoom] = useState(1);
  const [nativeOk, setNativeOk] = useState(true);
  const widgets = widgetsOf(layout);
  const selected = selectedWidgetPath?.length
    ? widgetAt(widgets, selectedWidgetPath)
    : undefined;
  const dragRef = useRef<{
    path: number[];
    startClientX: number;
    startClientY: number;
    originAbsX: number;
    originAbsY: number;
    parentAbsX: number;
    parentAbsY: number;
    lastAbsX: number;
    lastAbsY: number;
    moved: boolean;
  } | null>(null);

  const paint = useCallback(async () => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }
    let image: ImageData | null = null;
    if (nativeOk) {
      image = await lvglNativeRenderFrame(
        layout.display.width,
        layout.display.height,
        widgets,
        selected?.id,
      );
      if (!image) {
        setNativeOk(false);
      }
    }
    if (!image) {
      image = await lvglRenderFrame(
        layout.display.width,
        layout.display.height,
        widgets,
        selected?.id,
      );
    }
    if (!image) {
      return;
    }
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.putImageData(image, 0, 0);
    }
  }, [layout.display.width, layout.display.height, widgets, selected?.id, nativeOk]);

  useEffect(() => {
    let cancelled = false;
    paint().catch(() => undefined);
    return () => {
      cancelled = true;
      void cancelled;
    };
  }, [paint]);

  const canvasPoint = (clientX: number, clientY: number) => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return null;
    }
    const rect = canvas.getBoundingClientRect();
    const x = Math.floor((clientX - rect.left) / zoom);
    const y = Math.floor((clientY - rect.top) / zoom);
    return { x, y };
  };

  const onPointerDown = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (event.button !== 0) {
      return;
    }
    const pt = canvasPoint(event.clientX, event.clientY);
    if (!pt) {
      return;
    }
    const hit = hitTestLvglWidget(widgets, pt.x, pt.y);
    if (!hit) {
      onWidgetSelect?.({} as Widget, []);
      return;
    }
    onWidgetSelect?.(hit.widget as Widget, hit.path);
    const abs = absoluteOrigin(widgets, hit.path);
    const parentAbs = hit.path.length > 1
      ? absoluteOrigin(widgets, hit.path.slice(0, -1))
      : { x: 0, y: 0 };
    dragRef.current = {
      path: hit.path,
      startClientX: event.clientX,
      startClientY: event.clientY,
      originAbsX: abs.x,
      originAbsY: abs.y,
      parentAbsX: parentAbs.x,
      parentAbsY: parentAbs.y,
      lastAbsX: abs.x,
      lastAbsY: abs.y,
      moved: false,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const onPointerMove = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const drag = dragRef.current;
    if (!drag) {
      return;
    }
    const dx = Math.round((event.clientX - drag.startClientX) / zoom);
    const dy = Math.round((event.clientY - drag.startClientY) / zoom);
    if (!drag.moved && Math.abs(dx) < 2 && Math.abs(dy) < 2) {
      return;
    }
    drag.moved = true;
    const widget = widgetAt(widgets, drag.path);
    if (!widget) {
      return;
    }
    const ww = widget.width || 80;
    const wh = widget.height || 40;
    const absX = Math.max(0, Math.min(layout.display.width - ww, drag.originAbsX + dx));
    const absY = Math.max(0, Math.min(layout.display.height - wh, drag.originAbsY + dy));
    drag.lastAbsX = absX;
    drag.lastAbsY = absY;
    const next: Widget = {
      ...widget,
      x: absX - drag.parentAbsX,
      y: absY - drag.parentAbsY,
    };
    onWidgetChange?.(next, drag.path, dx, dy);
  };

  const onPointerUp = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const drag = dragRef.current;
    if (drag) {
      try {
        event.currentTarget.releasePointerCapture(event.pointerId);
      } catch {
        // ignore
      }
      if (drag.moved) {
        onWidgetDrop?.(drag.path, drag.lastAbsX, drag.lastAbsY);
      }
    }
    dragRef.current = null;
  };

  return (
    <Paper elevation={0} variant="outlined" sx={{ p: 1.5, height: '100%', overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="subtitle2" color="text.secondary">
          {t('layout.preview', { width: layout.display.width, height: layout.display.height })}
        </Typography>
        <Box>
          <Tooltip title={t('layout.zoomIn')}>
            <IconButton onClick={() => setZoom((z) => Math.min(z * 1.2, 3))} size="small">
              <ZoomIn />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('layout.zoomOut')}>
            <IconButton onClick={() => setZoom((z) => Math.max(z / 1.2, 0.25))} size="small">
              <ZoomOut />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('layout.zoomReset')}>
            <IconButton onClick={() => setZoom(1)} size="small">
              <Refresh />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: `${layout.display.height * Math.min(zoom, 1.2) + 40}px`,
          py: 1,
          position: 'relative',
        }}
      >
        <Box
          sx={{
            border: '10px solid #455a64',
            boxShadow: 'inset 0 0 0 1px #90a4ae, 0 8px 24px rgba(33,150,243,0.18)',
            lineHeight: 0,
            transform: `scale(${zoom})`,
            transformOrigin: 'center center',
            position: 'relative',
            backgroundColor: '#eef2f8',
            borderRadius: 4,
          }}
        >
          <canvas
            ref={canvasRef}
            width={layout.display.width}
            height={layout.display.height}
            style={{ display: 'block', cursor: 'crosshair', touchAction: 'none' }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
          />
          {widgets.length === 0 && emptyHint}
        </Box>
      </Box>

      <Typography variant="caption" color="text.secondary" display="block" mt={1}>
        {t(nativeOk ? 'layout.lvglEditorHint' : 'layout.lvglEditorHintFallback', { zoom: Math.round(zoom * 100) })}
      </Typography>
    </Paper>
  );
};

export default LvglCanvasEditor;
